import { CHAT_PAGE_SIZE } from '../config/constants';
import type { ChatStatusTab, ConversationFolder } from '../model/schema';

/**
 * Chat-list filter — cpanel `buildChatListFilter`, ported verbatim from
 * `features/chat/model/filters.ts`. Quirks pinned by `list-filter.test.ts`:
 *  - `skip` = 0 when viewing "my chats", else `SIZE * pageCount`;
 *  - `active: true` for the closed tabs (`status === '3'` or `'4'`) and not myChats —
 *    every other case leaves `active` undefined;
 *  - the `status` key is spread only when status !== 'ALL'.
 */

interface BuildChatListFilterArgs {
  search?: string | undefined;
  pageCount: number;
  status: string;
  type?: string | undefined;
  isAsc: boolean;
  myChats: boolean;
}

/** Wire params of `GET chats`. */
export interface ChatListFilter {
  skip: number;
  limit: number;
  username?: string | undefined;
  type?: string | undefined;
  order_by_urgency: boolean;
  my_chats: boolean;
  active?: boolean | undefined;
  status?: string | undefined;
}

export function buildChatListFilter({
  search,
  pageCount,
  status,
  type,
  isAsc,
  myChats,
}: BuildChatListFilterArgs): ChatListFilter {
  return {
    skip: myChats ? 0 : CHAT_PAGE_SIZE * pageCount,
    limit: CHAT_PAGE_SIZE,
    username: search,
    type,
    order_by_urgency: isAsc,
    my_chats: myChats,
    active: myChats || (status !== '3' && status !== '4') ? undefined : true,
    ...(status !== 'ALL' && { status }),
  };
}

/** The filter minus its page offset — the cache identity of an infinite list. */
export function chatListIdentity(filter: ChatListFilter): Omit<ChatListFilter, 'skip'> {
  return {
    limit: filter.limit,
    username: filter.username,
    type: filter.type,
    order_by_urgency: filter.order_by_urgency,
    my_chats: filter.my_chats,
    active: filter.active,
    status: filter.status,
  };
}

/** What a folder row narrows the list to. */
export interface FolderScope {
  /** Chat owner type sent as `type` (undefined = every owner). */
  type: 'CLIENT' | 'COURIER' | 'VENDOR' | undefined;
  /** `my_chats` — only chats assigned to the signed-in operator. */
  myChats: boolean;
  /** A status the folder fixes itself; undefined = the status tab decides. */
  fixedStatus: ChatStatusTab | undefined;
}

/**
 * Folder → filter preset. cpanel split the inbox into two routes (`/chat` =
 * COURIER, `/chat-vendor` = VENDOR) plus a "my chats" toggle and status tabs;
 * the new nav folds the first three into folders and pins the status for the
 * `open` (Новые) / `resolved` (Закрыто) folders.
 */
export function folderScope(folder: ConversationFolder): FolderScope {
  switch (folder) {
    case 'my':
      return { type: undefined, myChats: true, fixedStatus: undefined };
    case 'clients':
      return { type: 'CLIENT', myChats: false, fixedStatus: undefined };
    case 'couriers':
      return { type: 'COURIER', myChats: false, fixedStatus: undefined };
    case 'restaurants':
      return { type: 'VENDOR', myChats: false, fixedStatus: undefined };
    case 'open':
      return { type: undefined, myChats: false, fixedStatus: '1' };
    case 'resolved':
      return { type: undefined, myChats: false, fixedStatus: '3' };
  }
}

/** The status the list is actually filtered by: the folder's pin wins. */
export function effectiveStatus(
  folder: ConversationFolder,
  statusTab: ChatStatusTab,
): ChatStatusTab {
  return folderScope(folder).fixedStatus ?? statusTab;
}

interface BuildConversationListFilterArgs {
  folder: ConversationFolder;
  status: ChatStatusTab;
  search?: string | undefined;
  urgent?: boolean | undefined;
  pageCount: number;
}

/** The `GET chats` params for the URL state (`folder`, `status`, …) + a page. */
export function buildConversationListFilter({
  folder,
  status,
  search,
  urgent,
  pageCount,
}: BuildConversationListFilterArgs): ChatListFilter {
  const scope = folderScope(folder);
  const effective = effectiveStatus(folder, status);
  return buildChatListFilter({
    search,
    pageCount,
    status: effective,
    type: scope.type,
    isAsc: urgent === true,
    // Open (status 1) chats are unassigned, so `my_chats` would always be empty.
    myChats: scope.myChats && effective !== '1',
  });
}
